import React, { useMemo, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkNotificationsSeenMutation,
  useMarkAllNotificationsAsReadMutation,
} from "@/store/rest_api";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import type Notification from "@/model/interface/Notification";
import { useSeenOnScreen } from "@/hooks/useSeenOnScreen";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: unreadCount = 0 } = useGetUnreadNotificationCountQuery();
  const { data: notificationsPage } = useGetNotificationsQuery({ page: 0, size: 20 }, { skip: !open });
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();
  const [markSeen] = useMarkNotificationsSeenMutation();
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  const notifications = useMemo(() => notificationsPage?.items ?? [], [notificationsPage]);
  const hasUnread = unreadCount > 0 || notifications.some((notification) => notification.status !== "READ");
  const unseenIds = useMemo(() => {
    return notifications
      .filter((notification) => notification.status === "UNREAD")
      .map((notification) => notification.id);
  }, [notifications]);
  useSeenOnScreen(
    listRef,
    unseenIds,
    (ids) => {
      void markSeen(ids);
    },
    open,
  );

  const handleClick = (notification: Notification) => {
    if (notification.status !== "READ") {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-primary" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <h4 className="text-sm font-semibold">Notifications</h4>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => markAllAsRead()}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>
        <Separator />
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                data-item-id={n.id}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors ${
                  n.status !== "READ" ? "bg-muted/30" : ""
                }`}
                onClick={() => handleClick(n)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm truncate ${n.status !== "READ" ? "font-semibold" : ""}`}>{n.title}</p>
                    {n.status !== "READ" && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </div>
                  {n.message && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
